import type { ProductLine } from "@adpilot/shared";
import { IconChevronDown } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SearchInput } from "@/components/patterns/SearchInput";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export function ProductLineFilter({
  productLines,
  isLoading,
  isError,
  selected,
  onChange,
  onRetry,
}: {
  productLines: ProductLine[];
  isLoading: boolean;
  isError: boolean;
  selected: string[];
  onChange: (values: string[]) => void;
  onRetry: () => void;
}): React.ReactElement {
  const { t } = useTranslation("productCenter");
  const [search, setSearch] = useState("");
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const visibleLines = productLines.filter((line) =>
    `${line.name} ${line.shop?.name ?? ""}`.toLocaleLowerCase().includes(normalizedSearch),
  );
  const toggle = (value: string): void =>
    onChange(
      selected.includes(value)
        ? selected.filter((current) => current !== value)
        : [...selected, value],
    );
  const single = selected.length === 1 ? selected[0] : undefined;
  const singleLine = productLines.find((line) => line.id === single);
  const label =
    selected.length === 0
      ? t("toolbar.productLine")
      : single === "unassigned"
        ? t("toolbar.unassigned")
        : (singleLine?.name ?? t("toolbar.productLinesSelected", { count: selected.length }));

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(
              "max-w-[210px] gap-1.5 font-normal",
              selected.length > 0 && "border-transparent bg-control-highlight",
            )}
          />
        }
        aria-label={t("toolbar.productLineAria")}
      >
        <span className="truncate">{label}</span>
        <IconChevronDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      </PopoverTrigger>
      <PopoverPopup
        align="end"
        sideOffset={8}
        initialFocus={false}
        className="w-[300px] overflow-hidden p-0 [&>[data-slot=popover-viewport]]:p-0"
      >
        <div className="flex flex-col">
          <div className="border-b border-line p-2">
            <SearchInput
              showIcon
              className="w-full"
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              placeholder={t("toolbar.searchProductLines")}
              aria-label={t("toolbar.searchProductLines")}
            />
          </div>
          <div className="max-h-64 overflow-y-auto p-1">
            {isError ? (
              <div className="px-2 py-5">
                <ErrorState
                  variant="inline"
                  message={t("toolbar.productLinesLoadError")}
                  onRetry={onRetry}
                />
              </div>
            ) : isLoading ? (
              <div
                className="space-y-2 px-2 py-3"
                role="status"
                aria-label={t("toolbar.loadingProductLines")}
              >
                <span className="sr-only">{t("toolbar.loadingProductLines")}</span>
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-5/6" />
                <Skeleton className="h-8 w-2/3" />
              </div>
            ) : (
              <>
                <ProductLineFilterOption
                  checked={selected.includes("unassigned")}
                  label={t("toolbar.unassigned")}
                  onChange={() => toggle("unassigned")}
                />
                {visibleLines.map((line) => (
                  <ProductLineFilterOption
                    key={line.id}
                    checked={selected.includes(line.id)}
                    label={t("toolbar.productLineOption", {
                      name: [line.name, line.shop?.name, line.shop?.marketplaceName]
                        .filter(Boolean)
                        .join(" · "),
                      count: line.productCount,
                    })}
                    onChange={() => toggle(line.id)}
                  />
                ))}
              </>
            )}
            {!isLoading && !isError && visibleLines.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                {t("toolbar.noProductLines")}
              </p>
            ) : null}
          </div>
          {selected.length > 0 ? (
            <div className="border-t border-line p-2">
              <Button type="button" variant="ghost" size="xs" onClick={() => onChange([])}>
                {t("toolbar.clearProductLines")}
              </Button>
            </div>
          ) : null}
        </div>
      </PopoverPopup>
    </Popover>
  );
}

function ProductLineFilterOption({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: () => void;
}): React.ReactElement {
  return (
    <div className="flex min-h-9 items-center gap-2.5 rounded-[6px] px-2 hover:bg-subtle">
      <Checkbox checked={checked} onCheckedChange={onChange} aria-label={label} />
      <span className="min-w-0 flex-1 truncate text-xs">{label}</span>
    </div>
  );
}
